import Card from 'components/Card';
import LinkLookup from 'components/LinkLookup';
import Navbar from 'components/Navbar';
import SettingSidebar from 'components/Navbar/SettingsSidebar';
import Permissions from 'components/Permissions';
import { LEFT_ARROW_ICON } from 'constants/envelopeIcons';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { CustomerArtifactItemState } from 'reducers/fileUploads';
import { resetCustomerArtifactUploads, uploadCustomerArtifacts } from 'reducers/fileUploads';
import {
  getArtifactPresignError,
  getArtifactUploadBusy,
  getArtifactUploadItems,
} from 'selectors/fileUploads';
import { useSelector } from 'store';
import { NAV_BAR_HEIGHT_REM } from 'utils/dimensions';

const CustomerUploads: React.FC = () => {
  const dispatch = useDispatch();
  const [pickedFiles, setPickedFiles] = useState<File[]>([]);

  const items = useSelector(getArtifactUploadItems);
  const busy = useSelector(getArtifactUploadBusy);
  const presignError = useSelector(getArtifactPresignError);

  const warningBarOffset =
    document != null &&
    document.getElementById != null &&
    document.getElementById('customer-warning')?.offsetHeight;

  const onFilesSelected = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const list = event.target.files;

    if (list == null || list.length === 0) {
      return;
    }
    dispatch(resetCustomerArtifactUploads());
    setPickedFiles(Array.from(list));
    event.target.value = '';
  };

  const onUploadClick = (): void => {
    if (pickedFiles.length === 0) {
      return;
    }
    dispatch(uploadCustomerArtifacts({ files: pickedFiles }));
  };

  const displayRows: CustomerArtifactItemState[] =
    items.length > 0
      ? items
      : pickedFiles.map((f) => ({
          fileName: f.name,
          progress: null,
          status: 'pending',
        }));

  const header = (
    <div className="flex flex-row justify-between p-6">
      <div className="flex items-center gap-4">
        <LinkLookup routeName="customer" className="focus:outline-none">
          {LEFT_ARROW_ICON}
        </LinkLookup>
        <span className="text text--large text--bold">Uploads</span>
      </div>
    </div>
  );

  const body = (
    <div className="px-6 pb-8 flex flex-col gap-4 text-body">
      <div className="flex flex-wrap items-center gap-3 py-4">
        <label className="button button--secondary cursor-pointer" htmlFor="file-input">
          <span className="font-bold">Choose files</span>
          <input
            type="file"
            className="hidden"
            multiple
            onChange={onFilesSelected}
            id="file-input"
          />
        </label>
        <button
          type="button"
          className="button button--primary"
          disabled={busy || pickedFiles.length === 0}
          onClick={onUploadClick}
        >
          Upload to S3
        </button>
      </div>

      {presignError != null ? <p className="text-small text-red-600">{presignError}</p> : null}

      {displayRows.length > 0 ? (
        <ul className="flex flex-col gap-2 border border-litlingo-gray-2 rounded divide-y divide-litlingo-gray-2">
          {displayRows.map((slot, idx) => (
            <li
              key={slot.key != null ? slot.key : `${slot.fileName}-${idx}`}
              className="px-4 py-3 flex flex-col gap-1"
            >
              <div className="flex justify-between gap-4">
                <span className="font-medium truncate">{slot.fileName}</span>
                <span className="text-small text-litlingo-gray-2 shrink-0">
                  {slot.status === 'pending' && 'Ready'}
                  {slot.status === 'presigning' && 'Preparing…'}
                  {slot.status === 'uploading' && `${slot.progress ?? 0}%`}
                  {slot.status === 'done' && 'Done'}
                  {slot.status === 'error' && 'Failed'}
                </span>
              </div>
              {slot.key != null && slot.status === 'done' ? (
                <span className="text-xs text-litlingo-gray-2 break-all">{slot.key}</span>
              ) : null}
              {slot.status === 'error' && slot.errorMessage != null ? (
                <span className="text-xs text-red-600">{slot.errorMessage}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-small text-litlingo-gray-2 italic">No files selected.</p>
      )}
    </div>
  );

  return (
    <Permissions field="customers.config">
      <div className="bg-white">
        <Navbar />
        <div
          className="relative flex flex-row"
          style={{
            height: `calc(100vh - ${warningBarOffset ?? 0}px - ${NAV_BAR_HEIGHT_REM}rem)`,
          }}
        >
          <SettingSidebar />
          <div className="flex-1 overflow-auto litlingo-gray-bg-color-4">
            <div className="mx-auto max-w-5xl py-2">
              <Card items={[header, body]} />
            </div>
          </div>
        </div>
      </div>
    </Permissions>
  );
};

export default CustomerUploads;
